import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReactComponent as DefaultVehicleIcon } from '../../../../../assets/icons/car.svg';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon-v2.svg';
import { ReactComponent as TrashIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as PowerIcon } from '../../../../../assets/icons/power.svg';
// import { ReactComponent as VehicleIcon } from '../../../../../assets/icons/vehicle.svg';
import { useAppBranding } from '../../../../../config/branding';
import { useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomDropDown from '../../../../../shared-components/custom-drop-down/CustomDropDown';
import DeleteWarning from '../../../../../shared-components/delete-warning/DeleteWarning';
import ConfirmBox from '../../../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../../shared-components/switch-button-field/SwitchButton';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';
import SessionDetails from '../../EndConsumerProfile/SessionDetails';

interface ChargingDetailsInterface {
  remainingMinutes: number;
  totalDurationInMinutes: number;
  totalHours: number;
  totalPowerConsumedInKwh: number;
  totalSessions: number;
  totalSessionAmount: number;
}

interface VehicleDetailsInterface {
  batteryCapacity: string;
  commercialChargingDetails: ChargingDetailsInterface;
  connectorType: string;
  createdAt: string;
  deletedAt: string;
  isFavourite: boolean;
  isSelected: boolean;
  manufacturer: string;
  model: string;
  personalChargingDetails: ChargingDetailsInterface;
  updatedAt: string;
  userCustomId: string;
  vehicleImageUrl: string;
  _id: string;
}

/**
 * MyVehicle component
 * @returns - A React component for displaying vehicle information.
 */
function MyVehicle() {
  const branding = useAppBranding();
  const dispatch = useDispatch();
  const { t } = useTranslation('endConsumer');
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [vehicleList, setVehicleList] = useState<VehicleDetailsInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [modalView, setModalView] = useState({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => {},
  });
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelData, setModelData] = useState({
    title: '',
    content: <div />,
  });

  const selectedVehicleId = vehicleList.find((v) => v.isSelected)?._id;

  /**
   * Set primary vehicle function
   * @param key - The key of the toggle to activate.
   * @param index - The index of the vehicle in the data array.
   */
  const setPrimaryVehicle = (key: string, index: number) => {
    setIsLoading(true);
    try {
      endConsumerApi.setVehiclePrimary(vehicleList[index].userCustomId, {
        vehicleId: vehicleList[index]._id,
      });
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={t('vehicleSetAsPrimarySuccessfully')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setVehicleList((previousData) =>
              previousData.map((vehicle, vehicleIndex) =>
                vehicleIndex === index
                  ? {
                      ...vehicle,
                      isSelected: key === 'isSelected',
                    }
                  : {
                      ...vehicle,
                      isSelected: false,
                    },
              ),
            );
            setOpenStatus(false);
          }}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Handle toggle switch change.
   * @param key - The key of the toggle to activate.
   * @param index - The index of the vehicle in the data array.
   */
  const handleToggleSwitch = (key: string, index: number) => {
    setConfirmBox({
      status: true,
      title: t('setAsPrimary'),
      subTitle: t('areYouSureYouWantToMakeThisCarAsAPrimary'),
      /**
       * Set primary vehicle function
       * @param key - The key of the toggle to activate.
       * @param index - The index of the vehicle in the data array.
       */
      conformSubmit: () => {
        setPrimaryVehicle(key, index);
        setConfirmBox({ ...confirmBox, status: false });
      },
      submitLabel: t('ok'),
    });
  };

  /**
   * Close modal box function
   */
  const closeModal = () => {
    setOpenStatus(false);
  };

  /**
   * Handle success notification.
   * @param index - The index of the vehicle to delete.
   */
  const handleSuccess = (index: number) => {
    try {
      endConsumerApi.deleteVehicle(vehicleList[index].userCustomId, vehicleList[index]._id);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={t('vehicleDeletedSuccessfully')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setVehicleList((previousData) =>
              previousData.filter((_, vehicleIndex) => vehicleIndex !== index),
            );
            setOpenStatus(false);
          }}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Handle delete vehicle.
   * @param index - The index of the vehicle to delete.
   */
  const handleDeleteVehicle = (index: number) => {
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          title={t('areYouSureYouWantToDeleteCar')}
          description={t('deletingYourCarIsPermanentAndCannotBeUndone')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={closeModal}
          submitButtonLabel={t('delete')}
          submitButtonOnClick={() => handleSuccess(index)}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Personal Charger
   */
  const handleTotalSessionsCharger = (vehicle: string, type: string) => {
    setIsModelOpen(true);
    setModelData({
      title: t('sessionDetails'),
      content: <SessionDetails vehicle={vehicle} type={type} />,
    });
  };

  /**
   * Render vehicle details.
   * @param vehicle - The vehicle data.
   * @param index - The index of the vehicle in the data array.
   */
  const renderVehicleDetails = (
    vehicle: VehicleDetailsInterface & { originalIndex: number },
    index: number,
  ) => {
    return (
      <div className="vehicle-details">
        {isModelOpen && (
          <SidePaneModel
            open={isModelOpen}
            title={modelData.title}
            content={modelData.content}
            closePanel={() => setIsModelOpen(false)}
          />
        )}
        <div className="vehicle-details-wrap">
          <div className="vehicle-details-header">
            <p className="vehicle-details-header-title">{t('commercial')}</p>
            <p className="vehicle-details-header-amount">
              {ToFixedValue(vehicle.commercialChargingDetails?.totalSessionAmount)}
              <p className="currency-icon">({branding.text.currency})</p>
            </p>
          </div>
          <div className="vehicle-details-content">
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalPowerConsumed')}</p>
              <p className="vehicle-details-content-item-value">
                <PowerIcon className="icons" />{' '}
                {ToFixedValue(vehicle.commercialChargingDetails?.totalPowerConsumedInKwh)}
              </p>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalDuration')}</p>
              <div className="vehicle-details-content-item-value">
                <ClockIcon className="icons" />
                <p>
                  {Math.floor(
                    (vehicle.commercialChargingDetails?.totalDurationInMinutes || 0) / 60,
                  )}
                  h
                </p>
                <span>
                  {Math.round(
                    (vehicle.commercialChargingDetails?.totalDurationInMinutes || 0) % 60,
                  )}
                  m
                </span>
              </div>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('noOfSessions')}</p>
              <p
                className="vehicle-details-content-item-value underline"
                role="presentation"
                onClick={() => handleTotalSessionsCharger(vehicle._id, 'commercial')}>
                {vehicle.commercialChargingDetails?.totalSessions}
              </p>
            </div>
          </div>
        </div>
        <div className="vehicle-details-wrap">
          <div className="vehicle-details-header">
            <p className="vehicle-details-header-title">{t('personal')}</p>
          </div>
          <div className="vehicle-details-content">
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalPowerConsumed')}</p>
              <p className="vehicle-details-content-item-value">
                <PowerIcon className="icons" />{' '}
                {ToFixedValue(vehicle.personalChargingDetails?.totalPowerConsumedInKwh)}
              </p>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalDuration')}</p>
              <div className="vehicle-details-content-item-value">
                <ClockIcon className="icons " />
                <p>
                  {Math.floor((vehicle.personalChargingDetails?.totalDurationInMinutes || 0) / 60)}h
                </p>
                <span>
                  {Math.round((vehicle.personalChargingDetails?.totalDurationInMinutes || 0) % 60)}m
                </span>
              </div>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('noOfSessions')}</p>
              <p
                className="vehicle-details-content-item-value underline"
                role="presentation"
                onClick={() => handleTotalSessionsCharger(vehicle._id, 'personal')}>
                {vehicle.personalChargingDetails?.totalSessions}
              </p>
            </div>
          </div>
        </div>
        <div className="action-buttons">
          <div className="action-toggle">
            <p
              style={{
                color: vehicle.isSelected ? 'var(--primary-color)' : 'var(--black-color)',
                fontSize: '12px',
              }}>
              {t('setAsPrimary')}
            </p>
            <SwitchButton
              checked={vehicle.isSelected}
              disabled={vehicle.isSelected}
              change={() => handleToggleSwitch('isSelected', vehicle.originalIndex)}
            />
          </div>
          <div
            className="action-delete"
            role="presentation"
            onClick={() => handleDeleteVehicle(vehicle.originalIndex)}>
            <TrashIcon className="icons" />
            <p>{t('deleteVehicle')}</p>
          </div>
        </div>
        {index !== vehicleList.length - 1 && <div className="horizontal-line" />}
      </div>
    );
  };

  const sortedData = vehicleList
    .map((item, index) => ({ ...item, originalIndex: index }))
    .sort((a, b) => Number(b.isSelected) - Number(a.isSelected));

  /**
   * Fetches the list of vehicles for a specific user.
   * @param {string} userCustomId - The custom ID of the user.
   */
  const fetchVehicleList = async (userCustomId: string | undefined) => {
    setIsLoading(true);
    try {
      const response = await endConsumerApi.fetchVehicleList(userCustomId || '');
      setVehicleList(response.data);
      console.log(response.data);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    fetchVehicleList(selectedEndConsumer.userCustomId);
  }, [selectedEndConsumer?.userCustomId, selectedVehicleId]);

  return (
    <div className="vehicle-details-wrap">
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <ModalBox
            open={openStatus}
            title={modalView.title}
            content={modalView.content}
            closeModal={closeModal}
            header={modalView.header}
            rightCorner={modalView.rightCorner}
          />
          <ConfirmBox
            title={confirmBox.title}
            open={confirmBox.status}
            closeModal={() => setConfirmBox({ ...confirmBox, status: false })}
            subTitle={confirmBox.subTitle}
            conformSubmit={confirmBox.conformSubmit}
            submitLabel={confirmBox.submitLabel}
          />
          <p className="vehicle-details-title">{t('vehicleDetails')}</p>
          {sortedData.map((vehicle, index) => (
            <CustomDropDown
              icon={
                vehicle.vehicleImageUrl && vehicle.vehicleImageUrl !== 'default' ? (
                  <img
                    className="vehicle-icon"
                    src={`${vehicle.vehicleImageUrl}`}
                    alt={`${vehicle.manufacturer} ${vehicle.model}`}
                  />
                ) : (
                  <DefaultVehicleIcon className="vehicle-icon default-icon" />
                )
              }
              backgroundColor="plain"
              title={vehicle.manufacturer}
              description={vehicle.model}
              arrowPosition="right"
              height={50}
              content={renderVehicleDetails(vehicle as any, index)}
              open={index === 0}
            />
          ))}
        </>
      )}
    </div>
  );
}

export default MyVehicle;
